export const MAX_APP_FILE_COUNT = 500;
export const MAX_APP_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_APP_TOTAL_BYTES = 10 * 1024 * 1024;

// Keep synchronized with dropbox/companyos_app_platform/publish_validation.py.
const ALLOWED_EXTENSIONS = new Set([
  'css',
  'eot',
  'gif',
  'html',
  'ico',
  'jpeg',
  'jpg',
  'js',
  'json',
  'map',
  'mjs',
  'otf',
  'png',
  'svg',
  'ttf',
  'txt',
  'wasm',
  'webp',
  'woff',
  'woff2',
  'xml',
]);

const IGNORED_PATH_PARTS = new Set(['.DS_Store', 'Thumbs.db']);

type BrowserFile = File & {webkitRelativePath?: string};

export type UploadValidationIssue =
  | {code: 'empty'}
  | {code: 'invalid_name'}
  | {code: 'missing_index'}
  | {code: 'too_many_files'; limit: number}
  | {code: 'file_too_large'; name: string; limitBytes: number}
  | {code: 'total_too_large'; limitBytes: number}
  | {code: 'unsafe_path'; name: string}
  | {code: 'duplicate_path'; name: string}
  | {code: 'reserved_path'; name: string}
  | {code: 'unsupported_file'; name: string};

export type PreparedAppUpload = {
  files: File[];
  name: string;
  slug: string;
  totalBytes: number;
};

export type UploadValidationResult =
  | {ok: true; upload: PreparedAppUpload}
  | {ok: false; issues: UploadValidationIssue[]};

const filePath = (file: BrowserFile) => file.webkitRelativePath || file.name;

const isIgnoredFile = (file: BrowserFile) => {
  const parts = filePath(file).split('/');
  return parts.some((part) => part === '__MACOSX') || IGNORED_PATH_PARTS.has(parts.at(-1) || '');
};

const isUnsafePath = (path: string) => {
  const normalized = path.replaceAll('\\', '/');
  return (
    normalized.startsWith('/') ||
    /^[a-zA-Z]:\//.test(normalized) ||
    normalized.split('/').some((part) => part === '..')
  );
};

const extensionFor = (name: string) => {
  const basename = name.split('/').at(-1) || '';
  const extensionIndex = basename.lastIndexOf('.');
  return extensionIndex > 0 ? basename.slice(extensionIndex + 1).toLowerCase() : '';
};

export const appLogicalPath = (file: File, folderName: string) => {
  const path = filePath(file as BrowserFile).replaceAll('\\', '/');
  const prefix = `${folderName}/`;
  const relativePath = path.startsWith(prefix) ? path.slice(prefix.length) : path;
  return `/${relativePath}`;
};

export const slugifyAppName = (name: string) =>
  name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/g, '');

export const validateAppUpload = (selectedFiles: File[] | FileList): UploadValidationResult => {
  const files = Array.from(selectedFiles).filter((file) => !isIgnoredFile(file));
  if (!files.length) {
    return {ok: false, issues: [{code: 'empty'}]};
  }

  const firstFile = files[0] as BrowserFile;
  const folderName = firstFile.webkitRelativePath?.split('/')[0] || 'static-app';
  const slug = slugifyAppName(folderName);
  const issues: UploadValidationIssue[] = [];

  if (!slug) {
    issues.push({code: 'invalid_name'});
  }
  if (files.length > MAX_APP_FILE_COUNT) {
    issues.push({code: 'too_many_files', limit: MAX_APP_FILE_COUNT});
  }

  let totalBytes = 0;
  const logicalPaths = new Set<string>();
  for (const file of files) {
    const path = filePath(file);
    const logicalPath = appLogicalPath(file, folderName);
    totalBytes += file.size;

    if (isUnsafePath(path)) {
      issues.push({code: 'unsafe_path', name: path});
    }
    if (logicalPaths.has(logicalPath)) {
      issues.push({code: 'duplicate_path', name: logicalPath});
    }
    logicalPaths.add(logicalPath);
    if (
      logicalPath.toLowerCase() === '/_companyos' ||
      logicalPath.toLowerCase().startsWith('/_companyos/')
    ) {
      issues.push({code: 'reserved_path', name: logicalPath});
    }
    if (file.size > MAX_APP_FILE_BYTES) {
      issues.push({code: 'file_too_large', name: path, limitBytes: MAX_APP_FILE_BYTES});
    }
    if (!ALLOWED_EXTENSIONS.has(extensionFor(path))) {
      issues.push({code: 'unsupported_file', name: path});
    }
  }

  if (totalBytes > MAX_APP_TOTAL_BYTES) {
    issues.push({code: 'total_too_large', limitBytes: MAX_APP_TOTAL_BYTES});
  }

  if (!files.some((file) => appLogicalPath(file, folderName).toLowerCase() === '/index.html')) {
    issues.push({code: 'missing_index'});
  }

  if (issues.length) {
    return {ok: false, issues};
  }

  return {
    ok: true,
    upload: {
      files,
      name: folderName,
      slug,
      totalBytes,
    },
  };
};
